import React, { useCallback, useMemo, useState } from 'react';
import {
  Bar, CartesianGrid, ComposedChart, ErrorBar, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import KpiStrip from './shared/KpiStrip';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import { PALETTE, axisProps, fmtMoney, fmtMoneyShort, fmtNum, tooltipStyle } from './shared/chartTheme';

// Month by month, what the hospital spent and the care it gave, and what the next three months
// should bring if things carry on as they have. The server works every figure out (see
// FinancialOutlookService); this screen picks the department and lays the months side by side.
//
// Spending is what has been entered in Expenses. A cost paid in at least 4 of the last 6 complete
// months is regular and is expected to carry on at its average over the last 3; anything paid less
// often is occasional and is shown but never projected. Care (visits, bills, medicines delivered)
// is expected to carry on at its pace per day, which is an early estimate until 3 complete months
// have been recorded.

const HISTORY = [
  { key: 6, label: '6 months' },
  { key: 12, label: '12 months' },
  { key: 24, label: '24 months' },
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Months and days arrive as text ("2026-10", "2026-10-05") and are read as text: a date-only value
// put through new Date() lands on the day before east of Greenwich.
const monthShort = (ym) => MONTH_NAMES[Number(String(ym).split('-')[1]) - 1];
const monthLabel = (ym) => `${monthShort(ym)} ${String(ym).split('-')[0]}`;
const dayLabel = (iso) => {
  const [, m, d] = String(iso).split('-');
  return `${Number(d)} ${MONTH_NAMES[Number(m) - 1]}`;
};
const joinMonths = (yms) => {
  const names = (yms || []).map(monthShort);
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

const has = (v) => v !== null && v !== undefined;
const num = (v) => (has(v) ? Number(v) : null);
const money = (v) => (has(v) ? fmtMoney(v) : '—');

const pct = (v) => `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%`;

const trendShort = (p) => {
  if (!has(p)) return null;
  const n = Number(p);
  return n === 0 ? 'no change' : `${n > 0 ? 'up' : 'down'} ${Math.abs(n).toFixed(1)}%`;
};

/** How long the cash lasts, in a few words for a headline figure. */
const lastsText = (weeks) => {
  if (!has(weeks)) return null;
  const w = Number(weeks);
  if (w <= 0) return 'Nothing left';
  if (w < 1) return 'Lasts under a week';
  if (w < 8.7) {
    const n = Math.round(w);
    return `Lasts about ${n} week${n === 1 ? '' : 's'}`;
  }
  const m = Math.round(w / 4.348);
  return `Lasts about ${m} month${m === 1 ? '' : 's'}`;
};

const coverText = (weeks) => {
  if (!has(weeks)) return null;
  const w = Number(weeks);
  if (w <= 0) return 'Nothing left in hand';
  if (w < 1) return 'Less than a week of regular spending';
  if (w < 8.7) {
    const n = Math.round(w);
    return `About ${n} week${n === 1 ? '' : 's'} of regular spending`;
  }
  const m = Math.round(w / 4.348);
  return `About ${m} month${m === 1 ? '' : 's'} of regular spending`;
};

/** "Rs 246,014 to Rs 356,096", or nothing when the range is a single figure. */
const rangeText = (r, fmt = fmtMoney) => {
  if (!r || !has(r.low) || !has(r.high) || Number(r.low) === Number(r.high)) return null;
  return `${fmt(r.low)} to ${fmt(r.high)}`;
};

const changeTone = (v) => (v > 0 ? 'text-rose-600' : v < 0 ? 'text-emerald-600' : 'text-gray-500');

const MonthCell = ({ row }) => (
  <div className="whitespace-nowrap">
    <span className={row.kind === 'expected' ? 'text-gray-500' : 'font-semibold text-gray-900'}>{monthLabel(row.month)}</span>
    {row.kind === 'current' && <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">So far</span>}
    {row.kind === 'expected' && <span className="ml-2 text-[11px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">Expected</span>}
  </div>
);

/**
 * A figure in the month table: the month's own, the current month's so far with where it should
 * end, or a month ahead with its range.
 */
const FigureCell = ({ row, value, range, end, fmt, currentNote }) => {
  if (!has(value)) return <span className="text-gray-300">—</span>;
  if (row.kind === 'expected') {
    const r = rangeText(range, fmt);
    return (
      <div>
        <p className="italic text-gray-600 whitespace-nowrap">{fmt(value)}</p>
        {r && <p className="text-[11px] text-gray-400">{r}</p>}
      </div>
    );
  }
  return (
    <div>
      <p className="whitespace-nowrap">{fmt(value)}</p>
      {row.kind === 'current' && has(end) && <p className="text-[11px] text-gray-500">About {fmt(end)} by month end</p>}
      {row.kind === 'current' && !has(end) && currentNote && <p className="text-[11px] text-gray-500">{currentNote}</p>}
    </div>
  );
};

/** Bars for one figure by month: what happened, and lighter, what is expected with its range. */
const OutlookChart = ({ data, parts, expectedColor, expectedName, formatValue }) => (
  <ResponsiveContainer width="100%" height={280}>
    <ComposedChart data={data} margin={{ left: 8, right: 16, top: 8 }}>
      <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
      <XAxis dataKey="label" {...axisProps} minTickGap={12} />
      <YAxis {...axisProps} tickFormatter={formatValue === fmtMoney ? fmtMoneyShort : (v) => fmtNum(v)} />
      <Tooltip
        {...tooltipStyle}
        itemSorter={null}
        labelFormatter={(label, payload) => payload?.[0]?.payload?.tip || label}
        formatter={(v, name) => [formatValue(v), name]}
      />
      {/* In the order drawn, not alphabetical: what happened, then what is expected. */}
      <Legend itemSorter={null} />
      {parts.map((p) => (
        <Bar key={p.key} dataKey={p.key} name={p.name} stackId="month" fill={p.color} maxBarSize={44} />
      ))}
      <Bar dataKey="expected" name={expectedName} stackId="month" fill={expectedColor} fillOpacity={0.35} maxBarSize={44}>
        <ErrorBar dataKey="err" width={6} stroke="#6b7280" strokeWidth={1.2} />
      </Bar>
    </ComposedChart>
  </ResponsiveContainer>
);

const errOf = (r) => (r && has(r.low) && has(r.high)
  ? [Number(r.perMonth) - Number(r.low), Number(r.high) - Number(r.perMonth)]
  : undefined);

const FinancialOutlookReport = () => {
  const [history, setHistory] = useState(12);
  const [departmentName, setDepartmentName] = useState(null);

  const fetcher = useCallback(() => reportsAPI.getFinancialOutlook({ months: history }), [history]);
  const { data, loading } = useReport(fetcher);

  const access = data?.access || {};
  const departments = useMemo(() => data?.spending?.departments || [], [data]);
  const options = useMemo(() => {
    if (departments.length < 2) return departments;
    return [...departments, data.spending.all];
  }, [departments, data]);
  const dept = options.find((d) => d.name === departmentName) || options[0] || null;

  const care = data?.care || {};
  const basis = care.basis;
  const current = data?.currentMonth;
  const ahead = useMemo(() => data?.ahead || [], [data]);
  const basisMonths = useMemo(() => data?.basisMonths || [], [data]);
  const careNow = (current && care.monthly?.[current]) || null;

  // ------------------------------ month by month ------------------------------

  const monthRows = useMemo(() => {
    if (!data) return [];
    const rows = [];
    const spend = dept?.monthly || {};
    let previous = null;
    [...data.months, data.currentMonth].forEach((m) => {
      const c = data.care?.monthly?.[m];
      const s = spend[m];
      if (!has(c) && !has(s)) return;
      const isCurrent = m === data.currentMonth;
      const spent = num(s);
      rows.push({
        month: m,
        kind: isCurrent ? 'current' : 'month',
        spending: spent,
        change: !isCurrent && has(spent) && previous > 0 ? ((spent - previous) / previous) * 100 : null,
        visits: num(c?.visits),
        bills: num(c?.bills),
        fullPrice: num(c?.fullPrice),
        discount: num(c?.discount),
        charged: num(c?.charged),
        received: num(c?.received),
        end: isCurrent ? data.care?.byMonthEnd || {} : {},
      });
      if (!isCurrent && has(spent)) previous = spent;
    });
    ahead.forEach((m) => {
      const e = data.care?.expected?.[m] || {};
      rows.push({
        month: m,
        kind: 'expected',
        spending: num(dept?.expected?.perMonth),
        ranges: { spending: dept?.expected, ...e },
        visits: num(e.visits?.perMonth),
        bills: num(e.bills?.perMonth),
        fullPrice: num(e.fullPrice?.perMonth),
        discount: num(e.discount?.perMonth),
        charged: num(e.charged?.perMonth),
        received: num(e.received?.perMonth),
        end: {},
      });
    });
    return rows;
  }, [data, dept, ahead]);

  const figure = (key, fmt, label, extra = {}) => ({
    key,
    label,
    align: 'right',
    wrap: true,
    render: (r) => <FigureCell row={r} value={r[key]} range={r.ranges?.[key]} end={r.end?.[key]} fmt={fmt} {...extra} />,
    csv: (r) => r[key] ?? '',
    sortValue: (r) => r[key] ?? -1,
  });

  const monthColumns = useMemo(() => {
    const cols = [
      {
        key: 'month', label: 'Month',
        render: (r) => <MonthCell row={r} />,
        csv: (r) => `${monthLabel(r.month)}${r.kind === 'current' ? ' (so far)' : r.kind === 'expected' ? ' (expected)' : ''}`,
        searchText: (r) => monthLabel(r.month),
      },
    ];
    if (access.spending && dept) {
      cols.push(figure('spending', fmtMoney, `Spent (${dept.name})`, { currentNote: 'Entered so far' }));
      cols.push({
        key: 'change', label: 'On month before', align: 'right',
        render: (r) => (has(r.change)
          ? <span className={`font-semibold ${changeTone(r.change)}`}>{pct(r.change)}</span>
          : <span className="text-gray-300">—</span>),
        csv: (r) => (has(r.change) ? Number(r.change).toFixed(1) : ''),
        sortValue: (r) => r.change ?? -Infinity,
      });
    }
    cols.push(figure('visits', fmtNum, 'Visits'));
    cols.push(figure('bills', fmtNum, 'Bills'));
    cols.push(figure('fullPrice', fmtMoney, 'Care given (full price)'));
    cols.push(figure('charged', fmtMoney, 'Charged'));
    if (access.purchases) cols.push(figure('received', fmtMoney, 'Medicines received'));
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access.spending, access.purchases, dept]);

  // Print reads each cell's csv(), so its columns write the figures out in full: the figure on one
  // line, and under it in small type the range of a month ahead or where the current month should end.
  const monthPrintColumns = useMemo(() => monthColumns.map((c) => {
    if (c.key === 'month') return c;
    if (c.key === 'change') return { ...c, nowrap: true, csv: (r) => (has(r.change) ? pct(r.change) : '') };
    const fmt = ['visits', 'bills'].includes(c.key) ? fmtNum : fmtMoney;
    return {
      ...c,
      csv: (r) => {
        if (!has(r[c.key])) return '';
        const range = r.kind === 'expected' ? rangeText(r.ranges?.[c.key], fmt) : null;
        const end = r.kind === 'current' && has(r.end?.[c.key]) ? `${fmt(r.end[c.key])} by month end` : null;
        const note = range || end;
        return (
          <>
            <span style={{ whiteSpace: 'nowrap' }}>{fmt(r[c.key])}</span>
            {note && <div style={{ fontSize: '9px', color: '#666' }}>{note}</div>}
          </>
        );
      },
    };
  }), [monthColumns]);

  // ------------------------------ where the money goes ------------------------------

  const categoryRows = useMemo(() => dept?.categories || [], [dept]);
  const showDepartment = dept?.name === data?.spending?.all?.name;

  const categoryColumns = useMemo(() => {
    const cols = [
      {
        key: 'name', label: 'Category', wrap: true,
        render: (r) => (
          <div>
            <p className="font-semibold text-gray-900">{r.name}</p>
            {showDepartment && <p className="text-xs text-gray-500">{r.department}</p>}
          </div>
        ),
        csv: (r) => (showDepartment ? `${r.department}: ${r.name}` : r.name),
        searchText: (r) => `${r.name} ${r.department}`,
      },
      {
        key: 'regular', label: 'Kind',
        render: (r) => (
          <div>
            <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${r.regular ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
              {r.regular ? 'Regular' : 'Occasional'}
            </span>
            <p className="text-[11px] text-gray-500 mt-0.5">Paid {r.monthsPaid} of {r.monthsLooked} months</p>
          </div>
        ),
        csv: (r) => `${r.regular ? 'Regular' : 'Occasional'}, paid ${r.monthsPaid} of ${r.monthsLooked} months`,
        sortValue: (r) => (r.regular ? 1 : 0),
      },
      ...basisMonths.map((m) => ({
        key: `m-${m}`, label: monthLabel(m), align: 'right', nowrap: true,
        render: (r) => (has(r.lastThree?.[m]) ? fmtMoney(r.lastThree[m]) : <span className="text-gray-300">—</span>),
        csv: (r) => (has(r.lastThree?.[m]) ? fmtMoney(r.lastThree[m]) : ''),
        sortValue: (r) => Number(r.lastThree?.[m] ?? -1),
      })),
      {
        key: 'average', label: '3-month average', align: 'right', nowrap: true,
        render: (r) => (has(r.average) ? <span className="font-semibold">{fmtMoney(r.average)}</span> : <span className="text-gray-300">—</span>),
        csv: (r) => (has(r.average) ? fmtMoney(r.average) : ''),
        sortValue: (r) => Number(r.average ?? -1),
      },
      {
        key: 'expected', label: 'Expected a month', align: 'right',
        render: (r) => {
          if (r.expected) {
            const range = rangeText(r.expected);
            return (
              <div>
                <p className="font-semibold text-gray-900">{fmtMoney(r.expected.perMonth)}</p>
                {range && <p className="text-[11px] text-gray-500">{range}</p>}
              </div>
            );
          }
          return (
            <div>
              <p className="text-gray-500">Not projected</p>
              {has(r.occasionalAverage) && <p className="text-[11px] text-gray-400">Averaged {fmtMoney(r.occasionalAverage)} a month</p>}
            </div>
          );
        },
        csv: (r) => (r.expected
          ? fmtMoney(r.expected.perMonth)
          : `Not projected${has(r.occasionalAverage) ? ` (averaged ${fmtMoney(r.occasionalAverage)} a month)` : ''}`),
        sortValue: (r) => Number(r.expected?.perMonth ?? -1),
      },
    ];
    return cols;
  }, [basisMonths, showDepartment]);

  // ------------------------------ cash in hand ------------------------------

  const cashRows = useMemo(() => (departments.length > 1 ? [...departments, data.spending.all] : departments), [departments, data]);
  const cashColumns = [
    { key: 'name', label: 'Department', render: (r) => <span className="font-semibold text-gray-900">{r.name}</span> },
    { key: 'withdrawn', label: 'Drawn from the bank', align: 'right', nowrap: true, render: (r) => fmtMoney(r.cash.withdrawn), csv: (r) => fmtMoney(r.cash.withdrawn), sortValue: (r) => Number(r.cash.withdrawn) },
    { key: 'spent', label: 'Spent', align: 'right', nowrap: true, render: (r) => fmtMoney(r.cash.spent), csv: (r) => fmtMoney(r.cash.spent), sortValue: (r) => Number(r.cash.spent) },
    {
      key: 'inHand', label: 'Cash in hand', align: 'right', nowrap: true,
      render: (r) => <span className={`font-semibold ${Number(r.cash.inHand) < 0 ? 'text-rose-600' : 'text-gray-900'}`}>{fmtMoney(r.cash.inHand)}</span>,
      csv: (r) => fmtMoney(r.cash.inHand), sortValue: (r) => Number(r.cash.inHand),
    },
    {
      key: 'regular', label: 'Regular spending a month', align: 'right', nowrap: true,
      render: (r) => money(r.expected?.perMonth), csv: (r) => money(r.expected?.perMonth), sortValue: (r) => Number(r.expected?.perMonth ?? -1),
    },
    {
      key: 'cover', label: 'Lasts', wrap: true,
      render: (r) => coverText(r.coverWeeks) || <span className="text-gray-300">—</span>,
      csv: (r) => coverText(r.coverWeeks) || '', sortValue: (r) => Number(r.coverWeeks ?? -1),
    },
  ];

  // ------------------------------ charts ------------------------------

  const spendingChart = useMemo(() => {
    if (!dept || !data) return [];
    const regular = (dept.categories || []).filter((c) => c.regular);
    const occasional = (dept.categories || []).filter((c) => !c.regular);
    const total = (list, m) => list.reduce((s, c) => s + Number(c.monthly?.[m] || 0), 0);
    const rows = Object.keys(dept.monthly || {}).map((m) => ({
      label: monthLabel(m),
      tip: m === data.currentMonth ? `${monthLabel(m)}, entered so far` : monthLabel(m),
      regular: total(regular, m),
      occasional: total(occasional, m),
    }));
    ahead.forEach((m) => rows.push({
      label: monthLabel(m),
      tip: `${monthLabel(m)}, expected (regular costs)`,
      expected: num(dept.expected?.perMonth),
      err: errOf(dept.expected),
    }));
    return rows;
  }, [dept, data, ahead]);

  const careChart = useCallback((key) => {
    if (!data?.care?.monthly) return [];
    const rows = Object.entries(data.care.monthly).map(([m, c]) => {
      const isCurrent = m === data.currentMonth;
      const end = num(data.care.byMonthEnd?.[key]);
      const rest = isCurrent && has(end) ? Math.max(0, end - Number(c[key] || 0)) : undefined;
      return {
        label: monthLabel(m),
        tip: isCurrent ? `${monthLabel(m)}, so far and the rest of the month expected` : monthLabel(m),
        actual: Number(c[key] || 0),
        expected: rest,
      };
    });
    ahead.forEach((m) => {
      const e = data.care.expected?.[m]?.[key];
      rows.push({ label: monthLabel(m), tip: `${monthLabel(m)}, expected`, expected: num(e?.perMonth), err: errOf(e) });
    });
    return rows;
  }, [data, ahead]);

  // ------------------------------ headline figures ------------------------------

  const kpis = [];
  if (access.spending && dept) {
    const last = basisMonths.length ? `${monthShort(basisMonths[0])} to ${monthShort(basisMonths[basisMonths.length - 1])}` : '';
    const trend = trendShort(dept.trendPercent);
    kpis.push({
      label: 'Spent a month',
      value: money(dept.lastThreeAverage),
      sub: [last, trend].filter(Boolean).join(', '),
    });
    const e = dept.expected;
    kpis.push({
      label: `Next ${ahead.length} months`,
      value: e ? fmtMoney(Number(e.perMonth) * ahead.length) : '—',
      sub: e ? (rangeText({ low: Number(e.low) * ahead.length, high: Number(e.high) * ahead.length }) || 'Regular costs') : 'Nothing regular yet',
    });
    kpis.push({
      label: 'Cash in hand',
      value: fmtMoney(dept.cash.inHand),
      sub: lastsText(dept.coverWeeks) || 'Drawn, not yet spent',
      tone: Number(dept.cash.inHand) < 0 ? 'alert' : undefined,
    });
  }
  kpis.push({
    label: 'Care this month',
    value: careNow ? fmtMoney(careNow.fullPrice) : '—',
    sub: has(care.byMonthEnd?.fullPrice) ? `${fmtMoney(care.byMonthEnd.fullPrice)} by month end` : 'Full price of the bills',
  });
  kpis.push({
    label: 'Visits this month',
    value: careNow ? fmtNum(careNow.visits) : '—',
    sub: has(care.byMonthEnd?.visits) ? `${fmtNum(care.byMonthEnd.visits)} by month end` : undefined,
  });
  kpis.push({
    label: 'Care per visit',
    value: money(care.perVisit),
    sub: 'Full price of the bills',
  });

  const departmentLine = access.spending && dept ? ` · ${dept.name}` : '';
  const subtitle = data ? `As of ${data.asOf}${departmentLine} · ${history} months back, ${ahead.length} ahead` : '';

  return (
    <div className="space-y-5">
      <div className="flex gap-x-6 gap-y-2 flex-wrap items-center">
        <div className="flex gap-2 flex-wrap" role="group" aria-label="How far back">
          {HISTORY.map((h) => (
            <button
              key={h.key}
              type="button"
              aria-pressed={history === h.key}
              onClick={() => setHistory(h.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${history === h.key ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {h.label}
            </button>
          ))}
        </div>
        {access.spending && options.length > 1 && (
          <div className="flex gap-2 flex-wrap" role="group" aria-label="Department">
            {options.map((d) => (
              <button
                key={d.name}
                type="button"
                aria-pressed={dept?.name === d.name}
                onClick={() => setDepartmentName(d.name)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium ${dept?.name === d.name ? 'bg-emerald-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {d.name}
              </button>
            ))}
          </div>
        )}
        <div className="ml-auto"><PrintButton /></div>
      </div>

      <KpiStrip items={kpis} loading={loading} />

      {data && (
        <div className="space-y-2">
          <p className="text-sm text-gray-600 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
            {access.spending && (
              <>
                Spending is what has been entered in Expenses. A cost paid in at least 4 of the last 6
                months is regular, and is expected to carry on at its average over {joinMonths(basisMonths)};
                the range is the lowest and highest of those months. Occasional costs are listed but not
                projected, since nothing says when the next one comes
                {has(dept?.occasionalAverage) && Number(dept.occasionalAverage) > 0
                  ? `: over the last 6 months they averaged ${fmtMoney(dept.occasionalAverage)} a month on top of the regular ones. `
                  : '. '}
                This month is not part of it, as bills are often entered later. Spent a month is the
                average of {joinMonths(basisMonths)}, against the 3 months before.{' '}
              </>
            )}
            Visits and care are expected to carry on at their pace per day. Care given is the full price
            of every bill before anything was taken off.
            {access.purchases && ' Medicines received is what deliveries cost by the supplier\'s bill. It is not added to spending, since medicine bills are paid through Expenses.'}
          </p>
          {basis?.early && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Early estimate: visits and care are expected from the {fmtNum(basis.days)} days recorded
              from {dayLabel(basis.from)} to {dayLabel(basis.through)}. The estimate firms up once 3
              complete months have been recorded.
            </p>
          )}
          {!basis && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Too early to expect visits and care: a week of records is needed first.
            </p>
          )}
          {care.allFree && (
            <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              No bill in these months was charged anything: every bill was discounted in full, so care
              given is what the treatment would have cost at full price.
            </p>
          )}
          {!access.spending && (
            <p className="text-sm text-gray-500">Spending is shown to users who can open the Expenses dashboard.</p>
          )}
        </div>
      )}

      {access.spending && dept && spendingChart.length > 0 && (
        <ChartPanel title={`Spending by month: ${dept.name}`}>
          <OutlookChart
            data={spendingChart}
            parts={[
              { key: 'regular', name: 'Regular costs', color: PALETTE[0] },
              { key: 'occasional', name: 'Occasional costs', color: PALETTE[2] },
            ]}
            expectedColor={PALETTE[0]}
            expectedName="Expected (regular costs)"
            formatValue={fmtMoney}
          />
        </ChartPanel>
      )}

      {data && Object.keys(care.monthly || {}).length > 0 && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <ChartPanel title="Care given by month (full price)">
            <OutlookChart
              data={careChart('fullPrice')}
              parts={[{ key: 'actual', name: 'Care given', color: PALETTE[1] }]}
              expectedColor={PALETTE[1]}
              expectedName="Expected"
              formatValue={fmtMoney}
            />
          </ChartPanel>
          <ChartPanel title="Visits by month">
            <OutlookChart
              data={careChart('visits')}
              parts={[{ key: 'actual', name: 'Visits', color: PALETTE[3] }]}
              expectedColor={PALETTE[3]}
              expectedName="Expected"
              formatValue={fmtNum}
            />
          </ChartPanel>
        </div>
      )}

      <ReportTable
        title="Month by month"
        columns={monthColumns}
        rows={monthRows}
        csvName={`financial-outlook-${data?.asOf || ''}`}
        searchable={false}
        emptyText={loading ? 'Loading' : 'Nothing recorded yet'}
        noun="month"
        footnote="Expected months show the regular spending only; occasional costs come on top when they happen. The month still running shows what has been recorded so far."
      />

      {access.spending && dept && (
        <ReportTable
          title={`Where the money goes: ${dept.name}`}
          columns={categoryColumns}
          rows={categoryRows}
          csvName={`financial-outlook-categories-${data?.asOf || ''}`}
          emptyText="No expenses entered yet"
          noun="category"
          nounPlural="categories"
        />
      )}

      {access.spending && cashRows.length > 0 && (
        <ReportTable
          title="Cash in hand"
          columns={cashColumns}
          rows={cashRows}
          csvName={`financial-outlook-cash-${data?.asOf || ''}`}
          searchable={false}
          noun="department"
          footnote="Cash drawn from the bank and not yet spent, as Expenses counts it. Bank balances are not recorded here, so this is not the hospital's whole balance."
        />
      )}

      <ReportPrintLayout
        title="Financial Outlook"
        subtitle={subtitle}
        kpis={kpis.map(({ label, value, sub }) => ({ label, value: sub ? `${value} (${sub})` : value }))}
        tables={[
          { title: 'Month by month', columns: monthPrintColumns, rows: monthRows },
          ...(access.spending && dept ? [{ title: `Where the money goes: ${dept.name}`, columns: categoryColumns, rows: categoryRows }] : []),
          ...(access.spending && cashRows.length > 0 ? [{ title: 'Cash in hand', columns: cashColumns, rows: cashRows }] : []),
        ]}
        footnote="Regular costs (paid in at least 4 of the last 6 months) are expected at their average over the last 3 complete months, between the lowest and highest of them. Occasional costs are not projected. Visits and care are expected at their pace per day."
      />
    </div>
  );
};

export default FinancialOutlookReport;
